/**
 * Citation styles: curated + all CSL styles from
 * https://github.com/citation-style-language/styles
 * Curated entries override generated metadata for the same slug.
 */
import { GENERATED_CSL_STYLES } from "./citation-styles-generated";

export interface CitationStyleMeta {
  slug: string;
  name: string;
  shortName?: string;
  description: string;
  whenUsed: string;
  region?: string;
  /** Fetch path (e.g. "dependent/2d-materials"); root styles omit this. */
  cslPath?: string;
}

const CURATED_STYLES: CitationStyleMeta[] = [
  {
    slug: "apa",
    name: "American Psychological Association (APA) 7th edition",
    shortName: "APA 7",
    description:
      "APA style is used in psychology, education, and the social sciences. It uses an author-date in-text citation system and a reference list at the end.",
    whenUsed:
      "Psychology, education, social sciences, nursing, and business in the United States and many other countries.",
    region: "United States",
  },
  {
    slug: "modern-language-association",
    name: "Modern Language Association (MLA) 9th edition",
    shortName: "MLA 9",
    description:
      "MLA style is used in the humanities, especially literature and language. It uses parenthetical in-text citations keyed to a works cited list.",
    whenUsed:
      "Literature, languages, arts, and humanities in North America and internationally.",
    region: "United States",
  },
  {
    slug: "chicago-author-date",
    name: "Chicago Manual of Style (author-date)",
    shortName: "Chicago author-date",
    description:
      "Chicago author-date style uses in-text citations with author and year, plus a reference list. Common in the sciences and social sciences.",
    whenUsed:
      "Sciences, social sciences, and when a publisher or journal requires Chicago author-date.",
    region: "United States",
  },
  {
    // The CSL repository files it as `chicago-notes-bibliography` (plural). The
    // old singular slug matched nothing in the generated catalog, so this
    // curated entry was silently dropped: Chicago notes vanished from the
    // featured dropdown and every `/?style=…` deep link to it fell back to APA.
    slug: "chicago-notes-bibliography",
    name: "Chicago Manual of Style (notes and bibliography)",
    shortName: "Chicago notes",
    description:
      "Chicago notes and bibliography uses footnotes or endnotes plus a bibliography. Preferred in history and many humanities disciplines.",
    whenUsed:
      "History, arts, and humanities when footnotes or endnotes are required.",
    region: "United States",
  },
  {
    slug: "vancouver",
    name: "Vancouver",
    shortName: "Vancouver",
    description:
      "Vancouver style uses numbered references in the order of appearance. It is required by many medical and scientific journals.",
    whenUsed:
      "Medicine, biology, and health sciences; required by ICMJE and many medical journals.",
    region: "International",
  },
  {
    slug: "harvard1",
    name: "Harvard (Cite Them Right 11th edition)",
    shortName: "Harvard",
    description:
      "Harvard style uses author-date in-text citations and a reference list. Cite Them Right is a widely used UK variant.",
    whenUsed: "UK and Australian universities; business and social sciences.",
    region: "United Kingdom",
  },
  {
    slug: "ieee",
    name: "Institute of Electrical and Electronics Engineers (IEEE)",
    shortName: "IEEE",
    description:
      "IEEE style uses numbered references in square brackets, in order of citation. Common in engineering and computer science.",
    whenUsed:
      "Engineering, computer science, and electronics journals and conferences.",
    region: "International",
  },
  {
    slug: "american-medical-association",
    name: "American Medical Association (AMA)",
    shortName: "AMA",
    description:
      "AMA style uses superscript numbers for in-text citations and a numbered reference list. Used in medical publishing.",
    whenUsed: "Medical and health sciences journals in the United States.",
    region: "United States",
  },
  {
    slug: "nature",
    name: "Nature",
    shortName: "Nature",
    description:
      "Nature journal citation style uses numbered references. Used by Nature and some other scientific journals.",
    whenUsed:
      "When submitting to Nature or journals that follow Nature's format.",
    region: "International",
  },
  {
    slug: "dependent-acs-nano",
    name: "American Chemical Society (ACS)",
    shortName: "ACS",
    description:
      "ACS style uses numbers in superscript or parentheses. Used in chemistry and related fields.",
    whenUsed: "Chemistry, biochemistry, and chemical engineering journals.",
    region: "United States",
  },
  {
    slug: "dependent-turabian-author-date",
    name: "Turabian author-date",
    shortName: "Turabian",
    description:
      "Turabian is a student-friendly variant of Chicago. Author-date format uses in-text citations and a reference list.",
    whenUsed: "Student papers and theses when Chicago author-date is required.",
    region: "United States",
  },
  {
    slug: "bibtex",
    name: "BibTeX (plain)",
    shortName: "BibTeX",
    description:
      "BibTeX is a format for bibliography data used with LaTeX. This style produces plain text output suitable for bibliographies.",
    whenUsed:
      "LaTeX documents and when exporting or sharing bibliography data.",
    region: "International",
  },
  {
    slug: "cell",
    name: "Cell",
    shortName: "Cell",
    description:
      "Cell journal citation style. Used by Cell and some life sciences journals.",
    whenUsed: "When submitting to Cell or journals that follow this format.",
    region: "International",
  },
  {
    slug: "elsevier-harvard",
    name: "Elsevier Harvard",
    shortName: "Elsevier Harvard",
    description:
      "Elsevier's Harvard-style variant used in many Elsevier journals across disciplines.",
    whenUsed:
      "When an Elsevier journal specifies Harvard or author-date format.",
    region: "International",
  },
  {
    slug: "springer-basic-author-date",
    name: "Springer Basic",
    shortName: "Springer",
    description:
      "Springer's basic citation format. Used by many Springer and BMC journals.",
    whenUsed:
      "When submitting to Springer or BMC journals that use this style.",
    region: "International",
  },
  {
    slug: "american-political-science-association",
    name: "American Political Science Association (APSA)",
    shortName: "APSA",
    description:
      "APSA style is used in political science. It uses parenthetical author-date citations and a reference list.",
    whenUsed: "Political science and related disciplines in the United States.",
    region: "United States",
  },
  {
    slug: "american-sociological-association",
    name: "American Sociological Association (ASA)",
    shortName: "ASA",
    description:
      "ASA style is used in sociology. It uses author-date in-text citations and a reference list.",
    whenUsed: "Sociology and some social science journals.",
    region: "United States",
  },
  {
    slug: "american-anthropological-association",
    name: "American Anthropological Association (AAA)",
    shortName: "AAA",
    description:
      "AAA style is used in anthropology. It follows Chicago author-date with discipline-specific conventions.",
    whenUsed: "Anthropology journals and dissertations.",
    region: "United States",
  },
  {
    slug: "taylor-and-francis-council-of-science-editors-author-date",
    name: "Council of Science Editors (CSE)",
    shortName: "CSE",
    description:
      "CSE style offers name-year, citation-sequence, and citation-name systems. Used in natural sciences.",
    whenUsed:
      "Biology, geology, and other natural sciences when CSE is specified.",
    region: "International",
  },
  {
    slug: "taylor-and-francis-national-library-of-medicine",
    name: "National Library of Medicine (NLM)",
    shortName: "NLM",
    description:
      "NLM style is used in medicine and life sciences. It uses numbered references.",
    whenUsed: "Medical and life sciences when NLM format is required.",
    region: "United States",
  },
  // --- 100 additional styles (journals, publishers, associations) ---
  {
    slug: "apa-6th-edition",
    name: "APA 6th edition",
    shortName: "APA 6",
    description:
      "American Psychological Association 6th edition. Author-date in-text citations and reference list.",
    whenUsed:
      "Older psychology and social science requirements; check if 6th or 7th is specified.",
    region: "United States",
  },
  {
    slug: "chicago-notes-bibliography-17th-edition",
    name: "Chicago full note and bibliography",
    shortName: "Chicago full note",
    description:
      "Chicago notes and bibliography with full footnotes. Used in history and humanities.",
    whenUsed: "History and humanities when full footnotes are required.",
    region: "United States",
  },
  {
    slug: "harvard-cite-them-right",
    name: "Harvard (Cite Them Right)",
    shortName: "Harvard Cite Them Right",
    description:
      "Harvard author-date style per Cite Them Right. Common in UK and Australia.",
    whenUsed: "UK and Australian universities; business and social sciences.",
    region: "United Kingdom",
  },
  {
    slug: "ieee-with-url",
    name: "IEEE with URL",
    shortName: "IEEE (with URL)",
    description:
      "IEEE numbered style with URLs included in the reference list.",
    whenUsed: "Engineering and computer science when URLs are required.",
    region: "International",
  },
  {
    slug: "vancouver-superscript",
    name: "Vancouver (superscript)",
    shortName: "Vancouver superscript",
    description:
      "Vancouver style with superscript numbers for in-text citations.",
    whenUsed:
      "Medical and scientific journals requiring Vancouver with superscripts.",
    region: "International",
  },
  {
    slug: "american-chemical-society",
    name: "American Chemical Society (ACS) full",
    shortName: "ACS full",
    description:
      "ACS citation style for chemistry. Numbered or author-year depending on journal.",
    whenUsed: "Chemistry and biochemistry journals.",
    region: "United States",
  },
  {
    slug: "association-for-computing-machinery",
    name: "Association for Computing Machinery (ACM)",
    shortName: "ACM",
    description:
      "ACM style for computer science. Numbered references with optional URLs.",
    whenUsed: "ACM conferences and journals; computer science.",
    region: "International",
  },
  {
    slug: "american-physical-society-et-al",
    name: "American Physical Society (APS)",
    shortName: "APS",
    description:
      "APS style for physics. Numbered references in order of citation.",
    whenUsed: "Physical Review and other APS journals.",
    region: "United States",
  },
  {
    slug: "american-institute-of-physics",
    name: "American Institute of Physics (AIP)",
    shortName: "AIP",
    description:
      "AIP style for physics and applied science. Numbered references.",
    whenUsed: "AIP and partner physics journals.",
    region: "United States",
  },
  {
    slug: "royal-society-of-chemistry",
    name: "Royal Society of Chemistry (RSC)",
    shortName: "RSC",
    description:
      "RSC style for chemistry. Numbered or author-date depending on journal.",
    whenUsed: "RSC journals and chemistry in the UK.",
    region: "United Kingdom",
  },
  {
    slug: "biomed-central",
    name: "BioMed Central (BMC)",
    shortName: "BMC",
    description:
      "BMC citation style for open-access life sciences and medical journals.",
    whenUsed: "BioMed Central and Springer Nature BMC journals.",
    region: "International",
  },
  {
    slug: "dependent-plos-one",
    name: "PLOS ONE",
    shortName: "PLOS ONE",
    description:
      "PLOS ONE citation style. Numbered references with full journal names.",
    whenUsed: "PLOS ONE and other PLOS journals.",
    region: "International",
  },
  {
    slug: "frontiers",
    name: "Frontiers",
    shortName: "Frontiers",
    description:
      "Frontiers journal citation style. Used across Frontiers portfolio.",
    whenUsed:
      "Frontiers in Psychology, Neuroscience, and other Frontiers journals.",
    region: "International",
  },
  {
    slug: "the-lancet",
    name: "The Lancet",
    shortName: "Lancet",
    description: "Lancet citation style. Numbered references, Vancouver-like.",
    whenUsed: "The Lancet and Lancet family journals.",
    region: "International",
  },
  {
    slug: "bmj",
    name: "British Medical Journal (BMJ)",
    shortName: "BMJ",
    description:
      "BMJ citation style. Numbered references with specific formatting.",
    whenUsed: "BMJ and BMJ Open.",
    region: "United Kingdom",
  },
  {
    slug: "dependent-jama",
    name: "Journal of the American Medical Association (JAMA)",
    shortName: "JAMA",
    description: "JAMA citation style. Numbered references, similar to AMA.",
    whenUsed: "JAMA and JAMA Network journals.",
    region: "United States",
  },
  {
    slug: "science",
    name: "Science",
    shortName: "Science",
    description:
      "Science journal citation style. Numbered references. Points at the Science style — the society publishes no CSL style of its own.",
    whenUsed: "Science and Science Advances.",
    region: "International",
  },
  {
    slug: "elsevier-numeric",
    name: "Elsevier numeric",
    shortName: "Elsevier numeric",
    description:
      "Elsevier numeric citation style. Numbered references in order.",
    whenUsed: "Elsevier journals that use numeric style.",
    region: "International",
  },
  {
    slug: "elsevier-with-titles",
    name: "Elsevier with titles",
    shortName: "Elsevier with titles",
    description: "Elsevier style with article titles in the reference list.",
    whenUsed: "Elsevier journals requiring titles in references.",
    region: "International",
  },
  {
    slug: "springer-lecture-notes-in-computer-science",
    name: "Springer LNCS",
    shortName: "Springer LNCS",
    description:
      "Springer Lecture Notes in Computer Science. Author-number style.",
    whenUsed: "LNCS proceedings and some Springer CS journals.",
    region: "International",
  },
  {
    slug: "springer-mathphys-brackets",
    name: "Springer math and physics",
    shortName: "Springer mathphys",
    description: "Springer style for mathematics and physics journals.",
    whenUsed: "Springer math and physics journals.",
    region: "International",
  },
  {
    slug: "cambridge-university-press-author-date",
    name: "Cambridge University Press (author-date)",
    shortName: "Cambridge author-date",
    description: "Cambridge author-date style. Used by many CUP journals.",
    whenUsed: "Cambridge University Press journals and books.",
    region: "United Kingdom",
  },
  {
    slug: "oxford-university-press-author-date",
    name: "Oxford University Press (author-date)",
    shortName: "Oxford author-date",
    description: "Oxford author-date citation style.",
    whenUsed: "Oxford University Press journals.",
    region: "United Kingdom",
  },
  {
    slug: "oxford-university-press-note",
    name: "Oxford University Press (note)",
    shortName: "Oxford note",
    description: "Oxford footnote/note style for humanities.",
    whenUsed: "Oxford humanities and law journals.",
    region: "United Kingdom",
  },
  {
    slug: "wiley-vch-books",
    name: "Wiley-VCH books",
    shortName: "Wiley-VCH",
    description: "Wiley-VCH book citation style.",
    whenUsed: "Wiley-VCH chemistry and science books.",
    region: "International",
  },
  {
    slug: "wiley-author-date",
    name: "Wiley author-date",
    shortName: "Wiley author-date",
    description: "Wiley author-date style for journals.",
    whenUsed: "Wiley journals that use author-date.",
    region: "International",
  },
  {
    slug: "taylor-and-francis-chicago-author-date",
    name: "Taylor & Francis Chicago author-date",
    shortName: "T&F Chicago",
    description: "Taylor & Francis Chicago author-date variant.",
    whenUsed: "Taylor & Francis journals using Chicago author-date.",
    region: "International",
  },
  {
    slug: "taylor-and-francis-harvard",
    name: "Taylor & Francis Harvard",
    shortName: "T&F Harvard",
    description: "Taylor & Francis Harvard style.",
    whenUsed: "Taylor & Francis journals using Harvard.",
    region: "International",
  },
  {
    slug: "sage-harvard",
    name: "SAGE Harvard",
    shortName: "SAGE Harvard",
    description: "SAGE Publications Harvard citation style.",
    whenUsed: "SAGE journals in social sciences and education.",
    region: "International",
  },
  {
    slug: "sage-vancouver",
    name: "SAGE Vancouver",
    shortName: "SAGE Vancouver",
    description: "SAGE Vancouver-style numbered references.",
    whenUsed: "SAGE medical and health journals.",
    region: "International",
  },
  {
    slug: "sage-apa",
    name: "SAGE APA",
    shortName: "SAGE APA",
    description: "SAGE APA variant for social sciences.",
    whenUsed: "SAGE journals requiring APA.",
    region: "International",
  },
  {
    slug: "dependent-turabian-notes-bibliography",
    name: "Turabian full note and bibliography",
    shortName: "Turabian full note",
    description:
      "Turabian full footnote/endnote style. Student-friendly Chicago variant.",
    whenUsed: "Student papers and theses with footnotes.",
    region: "United States",
  },
  {
    slug: "cse-name-year",
    name: "CSE name-year",
    shortName: "CSE name-year",
    description:
      "Council of Science Editors name-year system. Author (Year) in text.",
    whenUsed: "Natural sciences when CSE name-year is specified.",
    region: "International",
  },
  {
    slug: "cse-citation-name",
    name: "CSE citation-name",
    shortName: "CSE citation-name",
    description:
      "CSE citation-name system. References alphabetically by author.",
    whenUsed: "Natural sciences when CSE citation-name is specified.",
    region: "International",
  },
  {
    slug: "cse-citation-sequence",
    name: "CSE citation-sequence",
    shortName: "CSE citation-sequence",
    description: "CSE citation-sequence. Numbered in order of first citation.",
    whenUsed: "Natural sciences when CSE citation-sequence is specified.",
    region: "International",
  },
  {
    slug: "acm-siggraph",
    name: "ACM SIGGRAPH",
    shortName: "ACM SIGGRAPH",
    description: "ACM SIGGRAPH conference and journal style.",
    whenUsed: "SIGGRAPH and graphics conferences.",
    region: "International",
  },
  {
    slug: "acm-sigchi-proceedings",
    name: "ACM SIGCHI",
    shortName: "ACM SIGCHI",
    description: "ACM SIGCHI style for human-computer interaction.",
    whenUsed: "CHI and HCI conferences and journals.",
    region: "International",
  },
  {
    slug: "american-mathematical-society-numeric",
    name: "American Mathematical Society (AMS)",
    shortName: "AMS",
    description: "AMS style for mathematics. Numbered or author-year.",
    whenUsed: "AMS journals and mathematical publications.",
    region: "United States",
  },
  {
    slug: "american-statistical-association",
    name: "American Statistical Association (ASA)",
    shortName: "ASA (stats)",
    description: "ASA style for statistics. Author-date in text.",
    whenUsed:
      "Journal of the American Statistical Association and ASA publications.",
    region: "United States",
  },
  {
    slug: "american-economic-association",
    name: "American Economic Association (AEA)",
    shortName: "AEA",
    description: "AEA style for economics. Author-date and reference list.",
    whenUsed: "American Economic Review and AEA journals.",
    region: "United States",
  },
  {
    slug: "american-historical-association",
    name: "American Historical Association (AHA)",
    shortName: "AHA",
    description:
      "AHA style for history. Footnote or author-date depending on journal.",
    whenUsed: "American Historical Review and history journals.",
    region: "United States",
  },
  {
    slug: "journal-of-the-american-philosophical-association",
    name: "American Philosophical Association (APA philosophy)",
    shortName: "APA (philosophy)",
    description: "APA style as used in philosophy (author-date).",
    whenUsed: "Philosophy journals in the United States.",
    region: "United States",
  },
  {
    slug: "annals-of-the-association-of-american-geographers",
    name: "American Association of Geographers (AAG)",
    shortName: "AAG",
    description: "AAG citation style for geography.",
    whenUsed: "Annals of the AAG and geography journals.",
    region: "United States",
  },
  {
    slug: "ecology",
    name: "Ecological Society of America (ESA)",
    shortName: "ESA",
    description:
      "ESA style for ecology. Author-date in text. Points at the Ecology style — the society publishes no CSL style of its own.",
    whenUsed: "Ecology and ESA journals.",
    region: "United States",
  },
  {
    slug: "the-geological-society-of-america",
    name: "Geological Society of America (GSA)",
    shortName: "GSA",
    description: "GSA style for geology. Author-date or numbered.",
    whenUsed: "GSA Bulletin and geology journals.",
    region: "United States",
  },
  {
    slug: "american-geophysical-union",
    name: "American Geophysical Union (AGU)",
    shortName: "AGU",
    description: "AGU style for geophysics and earth science.",
    whenUsed: "Journal of Geophysical Research and AGU journals.",
    region: "United States",
  },
  {
    slug: "american-meteorological-society",
    name: "American Meteorological Society (AMS)",
    shortName: "AMS (met)",
    description: "AMS style for atmospheric and oceanic sciences.",
    whenUsed: "AMS journals and weather/climate publications.",
    region: "United States",
  },
  {
    slug: "american-society-of-civil-engineers",
    name: "American Society of Civil Engineers (ASCE)",
    shortName: "ASCE",
    description: "ASCE citation style for civil engineering.",
    whenUsed: "ASCE journals and proceedings.",
    region: "United States",
  },
  {
    slug: "american-society-of-mechanical-engineers",
    name: "American Society of Mechanical Engineers (ASME)",
    shortName: "ASME",
    description: "ASME style for mechanical engineering.",
    whenUsed: "ASME journals and transactions.",
    region: "United States",
  },
  {
    slug: "american-institute-of-aeronautics-and-astronautics",
    name: "American Institute of Aeronautics and Astronautics (AIAA)",
    shortName: "AIAA",
    description: "AIAA style for aerospace engineering.",
    whenUsed: "AIAA journals and conferences.",
    region: "United States",
  },
  {
    slug: "society-for-neuroscience",
    name: "Society for Neuroscience (SfN)",
    shortName: "SfN",
    description: "Society for Neuroscience citation style.",
    whenUsed: "Journal of Neuroscience and SfN publications.",
    region: "United States",
  },
  {
    slug: "journal-of-biological-chemistry",
    name: "American Society for Biochemistry and Molecular Biology (ASBMB)",
    shortName: "ASBMB",
    description:
      "ASBMB style for biochemistry. Points at the the Journal of Biological Chemistry style — the society publishes no CSL style of its own.",
    whenUsed: "Journal of Biological Chemistry and ASBMB journals.",
    region: "United States",
  },
  {
    slug: "molecular-biology-of-the-cell",
    name: "American Society for Cell Biology (ASCB)",
    shortName: "ASCB",
    description:
      "ASCB citation style for cell biology. Points at the Molecular Biology of the Cell style — the society publishes no CSL style of its own.",
    whenUsed: "Molecular Biology of the Cell and ASCB publications.",
    region: "United States",
  },
  {
    slug: "dependent-the-american-journal-of-human-genetics",
    name: "American Society of Human Genetics (ASHG)",
    shortName: "ASHG",
    description: "ASHG style for human genetics.",
    whenUsed: "American Journal of Human Genetics.",
    region: "United States",
  },
  {
    slug: "american-heart-association",
    name: "American Heart Association (AHA)",
    shortName: "AHA (heart)",
    description: "AHA style for cardiology and cardiovascular research.",
    whenUsed: "Circulation and AHA journals.",
    region: "United States",
  },
  {
    slug: "dependent-diabetes-care",
    name: "American Diabetes Association (ADA)",
    shortName: "ADA",
    description:
      "ADA citation style for diabetes research. Points at the Diabetes Care style — the society publishes no CSL style of its own.",
    whenUsed: "Diabetes and ADA journals.",
    region: "United States",
  },
  {
    slug: "american-cancer-society",
    name: "American Cancer Society (ACS cancer)",
    shortName: "ACS cancer",
    description: "American Cancer Society style for cancer research.",
    whenUsed: "CA: A Cancer Journal for Clinicians and ACS publications.",
    region: "United States",
  },
  {
    slug: "american-psychiatric-association",
    name: "American Psychiatric Association (APA psych)",
    shortName: "APA psychiatry",
    description: "American Psychiatric Association citation style.",
    whenUsed: "American Journal of Psychiatry and psychiatric journals.",
    region: "United States",
  },
  {
    slug: "clinical-infectious-diseases",
    name: "Infectious Diseases Society of America (IDSA)",
    shortName: "IDSA",
    description:
      "IDSA style for infectious disease. Points at the Clinical Infectious Diseases style — the society publishes no CSL style of its own.",
    whenUsed: "Clinical Infectious Diseases and IDSA journals.",
    region: "United States",
  },
  {
    slug: "dependent-blood",
    name: "American Society of Hematology (ASH)",
    shortName: "ASH",
    description:
      "ASH citation style for hematology. Points at the Blood style — the society publishes no CSL style of its own.",
    whenUsed: "Blood and ASH publications.",
    region: "United States",
  },
  {
    slug: "radiology",
    name: "Radiological Society of North America (RSNA)",
    shortName: "RSNA",
    description:
      "RSNA style for radiology. Points at the Radiology style — the society publishes no CSL style of its own.",
    whenUsed: "Radiology and RSNA journals.",
    region: "United States",
  },
  {
    slug: "neurology",
    name: "American Academy of Neurology (AAN)",
    shortName: "AAN",
    description:
      "AAN citation style for neurology. Points at the Neurology style — the society publishes no CSL style of its own.",
    whenUsed: "Neurology and AAN publications.",
    region: "United States",
  },
  {
    slug: "journal-of-clinical-oncology",
    name: "American Society of Clinical Oncology (ASCO)",
    shortName: "ASCO",
    description:
      "ASCO style for oncology. Points at the the Journal of Clinical Oncology style — the society publishes no CSL style of its own.",
    whenUsed: "Journal of Clinical Oncology and ASCO publications.",
    region: "United States",
  },
  {
    slug: "american-association-for-cancer-research",
    name: "American Association for Cancer Research (AACR)",
    shortName: "AACR",
    description: "AACR citation style for cancer research.",
    whenUsed: "Cancer Research and AACR journals.",
    region: "United States",
  },
  {
    slug: "dependent-endocrinology",
    name: "Endocrine Society",
    shortName: "Endocrine Society",
    description:
      "Endocrine Society style for endocrinology. Points at the Endocrinology style — the society publishes no CSL style of its own.",
    whenUsed: "Endocrine Society journals.",
    region: "United States",
  },
  {
    slug: "dependent-optica",
    name: "Optica (formerly OSA)",
    shortName: "Optica",
    description: "Optica (Optical Society of America) citation style.",
    whenUsed: "Optics Express, JOSA, and Optica journals.",
    region: "United States",
  },
  {
    slug: "peerj",
    name: "PeerJ",
    shortName: "PeerJ",
    description:
      "PeerJ citation style. Open-access life and environmental sciences.",
    whenUsed: "PeerJ and PeerJ Computer Science.",
    region: "International",
  },
  {
    slug: "de-gruyter-author-date",
    name: "De Gruyter author-date",
    shortName: "De Gruyter author-date",
    description: "De Gruyter author-date citation style.",
    whenUsed: "De Gruyter journals using author-date.",
    region: "Germany",
  },
  {
    slug: "dependent-agronomy-journal",
    name: "American Society of Agronomy (ASA agronomy)",
    shortName: "ASA agronomy",
    description:
      "ASA style for agronomy and crop science. Points at the the Agronomy Journal style — the society publishes no CSL style of its own.",
    whenUsed: "Agronomy Journal and ASA publications.",
    region: "United States",
  },
  {
    slug: "american-society-for-microbiology",
    name: "American Society for Microbiology (ASM)",
    shortName: "ASM",
    description: "ASM style for microbiology.",
    whenUsed: "Journal of Bacteriology, mBio, and ASM journals.",
    region: "United States",
  },
  {
    slug: "the-plant-cell",
    name: "American Society of Plant Biologists (ASPB)",
    shortName: "ASPB",
    description:
      "ASPB citation style for plant biology. Points at the The Plant Cell style — the society publishes no CSL style of its own.",
    whenUsed: "The Plant Cell and Plant Physiology.",
    region: "United States",
  },
  {
    slug: "dependent-journal-of-ecology",
    name: "British Ecological Society (BES)",
    shortName: "BES",
    description:
      "BES style for ecology. UK-based. Points at the the Journal of Ecology style — the society publishes no CSL style of its own.",
    whenUsed: "Journal of Ecology and BES journals.",
    region: "United Kingdom",
  },
  {
    slug: "society-of-biblical-literature-fullnote-bibliography",
    name: "Society of Biblical Literature (SBL)",
    shortName: "SBL",
    description: "SBL citation style for biblical and religious studies.",
    whenUsed: "SBL publications and biblical studies.",
    region: "International",
  },
  {
    slug: "college-art-association",
    name: "College Art Association (CAA)",
    shortName: "CAA",
    description: "CAA style for art history and visual arts.",
    whenUsed: "Art Bulletin and CAA publications.",
    region: "United States",
  },
  {
    slug: "american-association-of-petroleum-geologists",
    name: "American Association of Petroleum Geologists (AAPG)",
    shortName: "AAPG",
    description: "AAPG style for petroleum geology.",
    whenUsed: "AAPG Bulletin and petroleum geology.",
    region: "United States",
  },
  {
    slug: "american-journal-of-respiratory-and-critical-care-medicine",
    name: "American Thoracic Society (ATS)",
    shortName: "ATS",
    description:
      "ATS citation style for pulmonary and critical care. Points at the the American Journal of Respiratory and Critical Care Medicine style — the society publishes no CSL style of its own.",
    whenUsed: "American Journal of Respiratory and Critical Care Medicine.",
    region: "United States",
  },
  {
    slug: "journal-of-the-american-society-of-nephrology",
    name: "American Society of Nephrology (ASN)",
    shortName: "ASN",
    description: "ASN style for nephrology.",
    whenUsed: "Journal of the American Society of Nephrology.",
    region: "United States",
  },
  {
    slug: "journal-of-animal-science",
    name: "American Society of Animal Science (ASAS)",
    shortName: "ASAS",
    description:
      "ASAS citation style for animal science. Points at the the Journal of Animal Science style — the society publishes no CSL style of its own.",
    whenUsed: "Journal of Animal Science.",
    region: "United States",
  },
  {
    slug: "dependent-crop-science",
    name: "Crop Science Society of America (CSSA)",
    shortName: "CSSA",
    description:
      "CSSA style for crop science. Points at the Crop Science style — the society publishes no CSL style of its own.",
    whenUsed: "Crop Science and CSSA publications.",
    region: "United States",
  },
  {
    slug: "dependent-soil-science-society-of-america-journal",
    name: "Soil Science Society of America (SSSA)",
    shortName: "SSSA",
    description: "SSSA citation style for soil science.",
    whenUsed: "Soil Science Society of America Journal.",
    region: "United States",
  },
  {
    slug: "dependent-geophysical-research-letters",
    name: "Geophysical Research Letters (GRL)",
    shortName: "GRL",
    description: "AGU Geophysical Research Letters style.",
    whenUsed: "Geophysical Research Letters.",
    region: "United States",
  },
  {
    slug: "society-for-research-in-child-development",
    name: "Society for Research in Child Development (SRCD)",
    shortName: "SRCD",
    description: "SRCD style for developmental psychology.",
    whenUsed: "Child Development and SRCD publications.",
    region: "United States",
  },
  {
    slug: "journal-of-the-association-for-information-systems",
    name: "Association for Information Systems (AIS)",
    shortName: "AIS",
    description: "AIS citation style for information systems.",
    whenUsed: "MIS Quarterly and AIS publications.",
    region: "International",
  },
  {
    slug: "american-nurses-association",
    name: "American Nurses Association (ANA)",
    shortName: "ANA",
    description: "ANA style for nursing publications.",
    whenUsed: "American Journal of Nursing and ANA.",
    region: "United States",
  },
  {
    slug: "dependent-american-journal-of-public-health",
    name: "American Public Health Association (APHA)",
    shortName: "APHA",
    description: "APHA citation style for public health.",
    whenUsed: "American Journal of Public Health.",
    region: "United States",
  },
  {
    slug: "dependent-annals-of-internal-medicine",
    name: "American College of Physicians (ACP)",
    shortName: "ACP",
    description:
      "ACP style for internal medicine. Points at the Annals of Internal Medicine style — the society publishes no CSL style of its own.",
    whenUsed: "Annals of Internal Medicine.",
    region: "United States",
  },
  {
    slug: "american-journal-of-roentgenology",
    name: "American Roentgen Ray Society (ARRS)",
    shortName: "ARRS",
    description: "ARRS style for radiology.",
    whenUsed: "American Journal of Roentgenology.",
    region: "United States",
  },
  {
    slug: "institute-of-mathematical-statistics",
    name: "Institute of Mathematical Statistics (IMS)",
    shortName: "IMS",
    description: "IMS style for statistics and probability.",
    whenUsed: "Annals of Statistics and IMS journals.",
    region: "International",
  },
  {
    slug: "anesthesiology",
    name: "American Society of Anesthesiologists (ASA anesth)",
    shortName: "ASA anesth",
    description:
      "ASA style for anesthesiology. Points at the Anesthesiology style — the society publishes no CSL style of its own.",
    whenUsed: "Anesthesiology and ASA publications.",
    region: "United States",
  },
  {
    slug: "american-speech-language-hearing-association",
    name: "American Speech-Language-Hearing Association (ASHA)",
    shortName: "ASHA",
    description: "ASHA citation style for speech and hearing.",
    whenUsed: "Journal of Speech, Language, and Hearing Research.",
    region: "United States",
  },
  {
    slug: "american-mineralogist",
    name: "Mineralogical Society of America (MSA)",
    shortName: "MSA",
    description:
      "MSA style for mineralogy. Points at the American Mineralogist style — the society publishes no CSL style of its own.",
    whenUsed: "American Mineralogist.",
    region: "United States",
  },
  {
    slug: "economic-geology",
    name: "Society of Economic Geologists (SEG)",
    shortName: "SEG",
    description:
      "SEG citation style for economic geology. Points at the Economic Geology style — the society publishes no CSL style of its own.",
    whenUsed: "Economic Geology.",
    region: "United States",
  },
  {
    slug: "american-journal-of-botany",
    name: "Botanical Society of America (BSA)",
    shortName: "BSA",
    description:
      "BSA style for botany. Points at the the American Journal of Botany style — the society publishes no CSL style of its own.",
    whenUsed: "American Journal of Botany.",
    region: "United States",
  },
  {
    slug: "journal-of-mammalogy",
    name: "American Society of Mammalogists (ASM mammals)",
    shortName: "ASM mammals",
    description:
      "ASM style for mammalogy. Points at the the Journal of Mammalogy style — the society publishes no CSL style of its own.",
    whenUsed: "Journal of Mammalogy.",
    region: "United States",
  },
  {
    slug: "american-fisheries-society",
    name: "American Fisheries Society (AFS)",
    shortName: "AFS",
    description: "AFS citation style for fisheries.",
    whenUsed: "Transactions of the American Fisheries Society.",
    region: "United States",
  },
  {
    slug: "the-auk",
    name: "American Ornithological Society (AOS)",
    shortName: "AOS",
    description:
      "AOS style for ornithology. Points at the The Auk style — the society publishes no CSL style of its own.",
    whenUsed: "The Auk and Ornithology.",
    region: "United States",
  },
  {
    slug: "conservation-biology",
    name: "Society for Conservation Biology (SCB)",
    shortName: "SCB",
    description:
      "SCB citation style for conservation biology. Points at the Conservation Biology style — the society publishes no CSL style of its own.",
    whenUsed: "Conservation Biology.",
    region: "International",
  },
  {
    slug: "american-musicological-society",
    name: "American Musicological Society (AMS music)",
    shortName: "AMS music",
    description: "AMS style for musicology.",
    whenUsed: "Journal of the American Musicological Society.",
    region: "United States",
  },
  {
    slug: "american-academy-of-religion",
    name: "American Academy of Religion (AAR)",
    shortName: "AAR",
    description: "AAR citation style for religious studies.",
    whenUsed: "Journal of the American Academy of Religion.",
    region: "United States",
  },
];

/** Curated metadata overrides for featured styles (merged into CITATION_STYLES). */
const CURATED_BY_SLUG = new Map(CURATED_STYLES.map((s) => [s.slug, s]));

// Keywords for intelligent region detection (used during style construction)
const REGION_DETECTION_KEYWORDS: Record<string, string[]> = {
  "United States": ["american", "united-states", "usa", "u-s-a"],
  "United Kingdom": [
    "british",
    "uk",
    "england",
    "scotland",
    "wales",
    "london",
    "oxford",
    "cambridge",
    "manchester",
    "edinburgh",
    "royal-society",
  ],
  Australia: ["australian", "australia", "sydney", "melbourne", "queensland"],
  Canada: ["canadian", "canada", "quebec", "ontario", "toronto"],
  Germany: [
    "german",
    "germany",
    "deutsch",
    "berlin",
    "munich",
    "munchen",
    "frankfurt",
    "heidelberg",
    "gottingen",
  ],
  France: [
    "french",
    "france",
    "francais",
    "paris",
    "lyon",
    "marseille",
    "bordeaux",
  ],
  Spain: [
    "spanish",
    "spain",
    "espanol",
    "espanola",
    "madrid",
    "barcelona",
    "valencia",
  ],
  Italy: [
    "italian",
    "italy",
    "italia",
    "italiano",
    "rome",
    "roma",
    "milan",
    "milano",
    "florence",
    "firenze",
    "venice",
    "venezia",
    "bologna",
    "napoli",
  ],
  Netherlands: [
    "dutch",
    "netherlands",
    "nederland",
    "amsterdam",
    "rotterdam",
    "utrecht",
    "leiden",
  ],
  Belgium: [
    "belgian",
    "belgium",
    "belgique",
    "belgie",
    "brussels",
    "bruxelles",
  ],
  Switzerland: [
    "swiss",
    "switzerland",
    "suisse",
    "schweiz",
    "zurich",
    "geneva",
    "bern",
  ],
  Austria: ["austrian", "austria", "osterreich", "vienna", "wien", "salzburg"],
  Sweden: [
    "swedish",
    "sweden",
    "sverige",
    "stockholm",
    "gothenburg",
    "uppsala",
  ],
  Norway: ["norwegian", "norway", "norge", "oslo", "bergen"],
  Denmark: ["danish", "denmark", "danmark", "copenhagen"],
  Finland: ["finnish", "finland", "suomi", "helsinki"],
  Japan: ["japanese", "japan", "nippon", "tokyo", "osaka", "kyoto"],
  China: ["chinese", "china", "beijing", "shanghai", "guangzhou", "sinica"],
  "South Korea": ["korean", "korea", "seoul"],
  Brazil: ["brazilian", "brazil", "brasil", "brasileira"],
  Mexico: ["mexican", "mexico", "mexicana"],
  India: ["indian", "india", "delhi", "mumbai", "bangalore"],
  "South Africa": [
    "south-african",
    "south-africa",
    "johannesburg",
    "cape-town",
  ],
  "New Zealand": ["new-zealand", "zealand"],
  Ireland: ["irish", "ireland", "dublin"],
  Poland: ["polish", "poland", "polska", "polskie", "warsaw", "krakow"],
  Portugal: ["portuguese", "portugal", "lisbon", "lisboa"],
  Russia: ["russian", "russia", "moscow", "moskva", "petersburg"],
  Turkey: ["turkish", "turkey", "turkiye", "ankara", "istanbul"],
  Israel: ["israeli", "israel", "jerusalem", "hebrew"],
  Singapore: ["singapore"],
  "Czech Republic": ["czech", "czechoslovak", "prague", "praha"],
  Hungary: ["hungarian", "hungary", "budapest", "magyar"],
  Romania: ["romanian", "romania", "bucharest"],
  Greece: ["greek", "greece", "hellenic", "athens"],
  Croatia: ["croatian", "croatia", "zagreb", "hrvatska"],
  Slovenia: ["slovenian", "slovenia", "ljubljana"],
  Argentina: ["argentine", "argentina", "buenos-aires"],
  Chile: ["chilean", "chile", "santiago"],
  Colombia: ["colombian", "colombia", "bogota"],
  Peru: ["peruvian", "peru", "peruana"],
};

/** Detect region from style slug and name */
function detectRegion(slug: string, name: string): string {
  const searchText = `${slug} ${name}`
    .toLowerCase()
    .replace(/-/g, " ")
    .replace(/[^a-z0-9\s]/g, "");

  for (const [region, keywords] of Object.entries(REGION_DETECTION_KEYWORDS)) {
    for (const keyword of keywords) {
      const normalizedKeyword = keyword.replace(/-/g, " ");
      if (searchText.includes(normalizedKeyword)) {
        return region;
      }
    }
  }

  return "International";
}

/**
 * Styles citation-js bundles itself. They have no file in the CSL repository —
 * `vancouver.csl` and `harvard1.csl` genuinely 404 there — so the generated
 * catalog cannot contain them, and merging by slug alone silently dropped two
 * of the styles the homepage advertises by name. They are appended explicitly.
 */
const ENGINE_BUILTIN_SLUGS = ["apa", "vancouver", "harvard1"];

/** All citation styles: generated list with curated overrides and detected regions. */
export const CITATION_STYLES: CitationStyleMeta[] = (() => {
  const merged = GENERATED_CSL_STYLES.map((g) => {
    const curated = CURATED_BY_SLUG.get(g.slug);
    if (curated) {
      return { ...g, ...curated };
    }
    // For non-curated styles, detect region if not already set
    const detectedRegion = detectRegion(g.slug, g.name);
    return {
      ...g,
      region: detectedRegion,
    };
  });
  const present = new Set(merged.map((s) => s.slug));
  const builtins = ENGINE_BUILTIN_SLUGS.map((slug) =>
    CURATED_BY_SLUG.get(slug)
  ).filter(
    (style): style is CitationStyleMeta =>
      style !== undefined && !present.has(style.slug)
  );
  return [...merged, ...builtins];
})();

/** Slugs for styles we generate programmatic pages for (all). */
export const CITATION_STYLE_SLUGS = CITATION_STYLES.map((s) => s.slug);

/** Curated slugs only (pre-rendered and shown in featured dropdown). */
export const FEATURED_STYLE_SLUGS = CURATED_STYLES.map((s) => s.slug);

/** Styles to show in the main dropdown (featured first; use for citation-style page + home). */
export function getFeaturedStyles(): CitationStyleMeta[] {
  return CITATION_STYLES.filter((s) => FEATURED_STYLE_SLUGS.includes(s.slug));
}

/**
 * Slug → style index, built once on first lookup.
 *
 * This used to be a linear `.find()` over all 10,834 styles. That is fine for
 * one call and quadratic for the things that actually call it: the build
 * pre-renders 10,834 /citation-style pages, and the AI surfaces resolve a style
 * per page and per API request.
 */
const STYLES_BY_SLUG = new Map<string, CitationStyleMeta>();

export function getCitationStyleBySlug(
  slug: string
): CitationStyleMeta | undefined {
  if (STYLES_BY_SLUG.size === 0) {
    for (const style of CITATION_STYLES) {
      STYLES_BY_SLUG.set(style.slug, style);
    }
  }
  return STYLES_BY_SLUG.get(slug);
}

// --- Region / country (for flags and country filter) ---

/** ISO 3166-1 alpha-2 country code per region. Missing or "International" → show globe. */
export const REGION_TO_COUNTRY: Record<string, string> = {
  "United States": "US",
  "United Kingdom": "GB",
  Germany: "DE",
  Australia: "AU",
  Canada: "CA",
  France: "FR",
  Spain: "ES",
  Italy: "IT",
  Netherlands: "NL",
  Belgium: "BE",
  Switzerland: "CH",
  Austria: "AT",
  Sweden: "SE",
  Norway: "NO",
  Denmark: "DK",
  Finland: "FI",
  Japan: "JP",
  China: "CN",
  "South Korea": "KR",
  Brazil: "BR",
  Mexico: "MX",
  India: "IN",
  "South Africa": "ZA",
  "New Zealand": "NZ",
  Ireland: "IE",
  Poland: "PL",
  Portugal: "PT",
  Russia: "RU",
  Turkey: "TR",
  Israel: "IL",
  Singapore: "SG",
  "Hong Kong": "HK",
  Taiwan: "TW",
  Malaysia: "MY",
  Indonesia: "ID",
  Thailand: "TH",
  Vietnam: "VN",
  Philippines: "PH",
  Argentina: "AR",
  Chile: "CL",
  Colombia: "CO",
  Peru: "PE",
  Venezuela: "VE",
  "Czech Republic": "CZ",
  Hungary: "HU",
  Romania: "RO",
  Greece: "GR",
  Ukraine: "UA",
  Croatia: "HR",
  Slovenia: "SI",
  Slovakia: "SK",
  Serbia: "RS",
  Bulgaria: "BG",
  Lithuania: "LT",
  Latvia: "LV",
  Estonia: "EE",
  Iceland: "IS",
  Luxembourg: "LU",
  Malta: "MT",
  Cyprus: "CY",
  "Saudi Arabia": "SA",
  "United Arab Emirates": "AE",
  Egypt: "EG",
  Nigeria: "NG",
  Kenya: "KE",
  Morocco: "MA",
  Pakistan: "PK",
  Bangladesh: "BD",
  "Sri Lanka": "LK",
};

/** URL slug for region (e.g. "United States" → "united-states"). */
export function getRegionSlug(region: string): string {
  return region.toLowerCase().replace(/\s+/g, "-");
}

/**
 * Lowercased ISO 3166-1 alpha-2 code for a region, or null for
 * International / unknown. Doubles as the `public/flags/<code>.svg` basename.
 *
 * This deliberately does not return a flag *emoji*. Windows ships no flag
 * glyphs in Segoe UI Emoji, so Chrome and Edge on Windows render a
 * regional-indicator pair as the two bare letters ("us" beside "APA 7"). Flags
 * are drawn from vendored SVGs instead — see `RegionFlag` and
 * `scripts/generate-flags.mjs`.
 */
export function getRegionCountryCode(
  region: string | undefined
): string | null {
  if (!region || region === "International") {
    return null;
  }
  const code = REGION_TO_COUNTRY[region];
  if (!code || code.length !== 2) {
    return null;
  }
  return code.toLowerCase();
}

/** Unique regions with slug, for country filter pages. */
export const REGIONS = [
  // Major English-speaking countries
  { slug: "united-states", name: "United States" },
  { slug: "united-kingdom", name: "United Kingdom" },
  { slug: "australia", name: "Australia" },
  { slug: "canada", name: "Canada" },
  { slug: "new-zealand", name: "New Zealand" },
  { slug: "ireland", name: "Ireland" },
  { slug: "south-africa", name: "South Africa" },
  // Western Europe
  { slug: "germany", name: "Germany" },
  { slug: "france", name: "France" },
  { slug: "spain", name: "Spain" },
  { slug: "italy", name: "Italy" },
  { slug: "netherlands", name: "Netherlands" },
  { slug: "belgium", name: "Belgium" },
  { slug: "switzerland", name: "Switzerland" },
  { slug: "austria", name: "Austria" },
  { slug: "portugal", name: "Portugal" },
  { slug: "luxembourg", name: "Luxembourg" },
  // Scandinavia
  { slug: "sweden", name: "Sweden" },
  { slug: "norway", name: "Norway" },
  { slug: "denmark", name: "Denmark" },
  { slug: "finland", name: "Finland" },
  { slug: "iceland", name: "Iceland" },
  // Eastern Europe
  { slug: "poland", name: "Poland" },
  { slug: "czech-republic", name: "Czech Republic" },
  { slug: "hungary", name: "Hungary" },
  { slug: "romania", name: "Romania" },
  { slug: "russia", name: "Russia" },
  { slug: "ukraine", name: "Ukraine" },
  { slug: "greece", name: "Greece" },
  { slug: "turkey", name: "Turkey" },
  { slug: "croatia", name: "Croatia" },
  { slug: "slovenia", name: "Slovenia" },
  { slug: "slovakia", name: "Slovakia" },
  { slug: "serbia", name: "Serbia" },
  { slug: "bulgaria", name: "Bulgaria" },
  { slug: "lithuania", name: "Lithuania" },
  { slug: "latvia", name: "Latvia" },
  { slug: "estonia", name: "Estonia" },
  // Asia
  { slug: "japan", name: "Japan" },
  { slug: "china", name: "China" },
  { slug: "south-korea", name: "South Korea" },
  { slug: "india", name: "India" },
  { slug: "singapore", name: "Singapore" },
  { slug: "hong-kong", name: "Hong Kong" },
  { slug: "taiwan", name: "Taiwan" },
  { slug: "malaysia", name: "Malaysia" },
  { slug: "indonesia", name: "Indonesia" },
  { slug: "thailand", name: "Thailand" },
  { slug: "vietnam", name: "Vietnam" },
  { slug: "philippines", name: "Philippines" },
  { slug: "pakistan", name: "Pakistan" },
  { slug: "bangladesh", name: "Bangladesh" },
  { slug: "sri-lanka", name: "Sri Lanka" },
  // Middle East
  { slug: "israel", name: "Israel" },
  { slug: "saudi-arabia", name: "Saudi Arabia" },
  { slug: "united-arab-emirates", name: "United Arab Emirates" },
  // Latin America
  { slug: "brazil", name: "Brazil" },
  { slug: "mexico", name: "Mexico" },
  { slug: "argentina", name: "Argentina" },
  { slug: "chile", name: "Chile" },
  { slug: "colombia", name: "Colombia" },
  { slug: "peru", name: "Peru" },
  { slug: "venezuela", name: "Venezuela" },
  // Africa
  { slug: "egypt", name: "Egypt" },
  { slug: "nigeria", name: "Nigeria" },
  { slug: "kenya", name: "Kenya" },
  { slug: "morocco", name: "Morocco" },
  // International (catch-all)
  { slug: "international", name: "International" },
] as const;

/** Styles whose region normalizes to this slug (e.g. "united-states", "international"). */
export function getStylesByRegionSlug(regionSlug: string): CitationStyleMeta[] {
  return CITATION_STYLES.filter(
    (s) => getRegionSlug(s.region ?? "International") === regionSlug
  );
}

// --- Category / type (expanded for better SEO coverage) ---

export const CATEGORIES = [
  // Health & Medical (split from generic "Medicine")
  { slug: "medicine", name: "Medicine", emoji: "⚕️" },
  { slug: "nursing", name: "Nursing", emoji: "👩‍⚕️" },
  { slug: "public-health", name: "Public Health", emoji: "🏥" },
  { slug: "pharmacy", name: "Pharmacy", emoji: "💊" },
  { slug: "dentistry", name: "Dentistry", emoji: "🦷" },
  { slug: "veterinary", name: "Veterinary Science", emoji: "🐾" },
  // Psychology & Social Sciences
  { slug: "psychology", name: "Psychology", emoji: "🧠" },
  { slug: "sociology", name: "Sociology", emoji: "👥" },
  { slug: "anthropology", name: "Anthropology", emoji: "🗿" },
  { slug: "political-science", name: "Political Science", emoji: "🏛️" },
  { slug: "economics", name: "Economics", emoji: "📈" },
  { slug: "education", name: "Education", emoji: "🎓" },
  { slug: "social-work", name: "Social Work", emoji: "🤝" },
  // Natural Sciences
  { slug: "biology", name: "Biology", emoji: "🧬" },
  { slug: "chemistry", name: "Chemistry", emoji: "🧪" },
  { slug: "physics", name: "Physics", emoji: "⚛️" },
  { slug: "environmental-science", name: "Environmental Science", emoji: "🌍" },
  { slug: "earth-science", name: "Earth Science & Geology", emoji: "🪨" },
  { slug: "agriculture", name: "Agriculture", emoji: "🌾" },
  { slug: "ecology", name: "Ecology", emoji: "🌿" },
  // Engineering & Technology
  { slug: "engineering", name: "Engineering", emoji: "⚙️" },
  { slug: "computer-science", name: "Computer Science", emoji: "💻" },
  { slug: "mathematics", name: "Mathematics", emoji: "🔢" },
  { slug: "statistics", name: "Statistics", emoji: "📊" },
  // Humanities
  { slug: "history", name: "History", emoji: "📜" },
  { slug: "literature", name: "Literature", emoji: "📚" },
  { slug: "philosophy", name: "Philosophy", emoji: "🤔" },
  { slug: "linguistics", name: "Linguistics", emoji: "🗣️" },
  { slug: "art-history", name: "Art History", emoji: "🎨" },
  { slug: "music", name: "Music", emoji: "🎵" },
  { slug: "film-media", name: "Film & Media", emoji: "🎬" },
  { slug: "archaeology", name: "Archaeology", emoji: "⛏️" },
  // Professional Fields
  { slug: "law", name: "Law", emoji: "⚖️" },
  { slug: "business", name: "Business", emoji: "💼" },
  { slug: "architecture", name: "Architecture", emoji: "🏗️" },
  // Religion & Theology
  { slug: "religion", name: "Religion & Theology", emoji: "✝️" },
  // Geography
  { slug: "geography", name: "Geography", emoji: "🗺️" },
  // Other
  { slug: "general", name: "General / Multidisciplinary", emoji: "📖" },
  { slug: "other", name: "Other", emoji: "📄" },
] as const;

// Keywords for intelligent category detection
const CATEGORY_KEYWORDS: Record<string, string[]> = {
  medicine: [
    "medical",
    "medicine",
    "clinical",
    "hospital",
    "surgery",
    "surgical",
    "physician",
    "pathology",
    "oncology",
    "cancer",
    "cardiology",
    "cardiac",
    "heart",
    "neurology",
    "neuroscience",
    "radiology",
    "pediatric",
    "paediatric",
    "geriatric",
    "orthopedic",
    "orthopaedic",
    "dermatology",
    "ophthalmology",
    "urology",
    "nephrology",
    "gastroenterology",
    "hematology",
    "haematology",
    "endocrine",
    "diabetes",
    "rheumatology",
    "immunology",
    "allergy",
    "anesthesia",
    "anaesthesia",
    "intensive-care",
    "critical-care",
    "emergency",
    "trauma",
    "rehabilitation",
    "physical-medicine",
    "jama",
    "lancet",
    "bmj",
    "nejm",
    "health",
    "healthcare",
    "therapeutics",
    "disease",
    "diagnosis",
    "treatment",
    "patient",
    "respiratory",
    "pulmonary",
    "infectious",
    "hepatology",
    "obesity",
    "nutrition-medical",
    "pain",
    "palliative",
    "hospice",
    "transplant",
    "vascular",
    "thoracic",
    "plastic-surgery",
  ],
  nursing: [
    "nursing",
    "nurse",
    "midwifery",
    "midwife",
    "neonatal",
    "perinatal",
    "maternal",
  ],
  "public-health": [
    "public-health",
    "epidemiology",
    "epidemiologic",
    "preventive-medicine",
    "community-health",
    "global-health",
    "health-policy",
    "occupational-health",
    "environmental-health",
    "biostatistics",
    "population-health",
  ],
  pharmacy: [
    "pharmacy",
    "pharmaceutical",
    "pharmacology",
    "pharmacotherapy",
    "drug",
    "medication",
    "toxicology",
  ],
  dentistry: [
    "dental",
    "dentistry",
    "orthodontic",
    "periodontic",
    "endodontic",
    "prosthodontic",
    "oral",
  ],
  veterinary: ["veterinary", "animal-health", "zoological-medicine"],
  psychology: [
    "psychology",
    "psychological",
    "psycholog",
    "psychiatry",
    "psychiatric",
    "mental-health",
    "cognitive",
    "behavioral",
    "behavioural",
    "developmental",
    "social-psychology",
    "neuropsychology",
    "psychotherapy",
    "counseling",
    "counselling",
    "apa",
  ],
  sociology: [
    "sociology",
    "sociological",
    "social-science",
    "society",
    "social-research",
  ],
  anthropology: [
    "anthropology",
    "anthropological",
    "ethnography",
    "ethnographic",
    "cultural-studies",
  ],
  "political-science": [
    "political",
    "politics",
    "government",
    "policy",
    "public-policy",
    "international-relations",
    "diplomacy",
    "political-science",
  ],
  economics: [
    "economics",
    "economic",
    "econometric",
    "finance",
    "financial",
    "monetary",
    "fiscal",
    "macroeconomic",
    "microeconomic",
    "development-economics",
  ],
  education: [
    "education",
    "educational",
    "pedagogy",
    "teaching",
    "learning",
    "curriculum",
    "instructional",
    "school",
    "higher-education",
    "k-12",
  ],
  "social-work": [
    "social-work",
    "welfare",
    "human-services",
    "child-welfare",
    "family-services",
  ],
  biology: [
    "biology",
    "biological",
    "biolog",
    "cell",
    "molecular",
    "genetics",
    "genetic",
    "genomics",
    "proteomics",
    "biochemistry",
    "biochem",
    "microbiology",
    "microb",
    "bacteriology",
    "virology",
    "parasitology",
    "mycology",
    "biotechnology",
    "biotech",
    "bioinformatics",
    "systems-biology",
    "developmental-biology",
    "neurobiology",
    "physiology",
    "anatomy",
    "zoology",
    "zoological",
    "entomology",
    "ornithology",
    "mammalogy",
    "herpetology",
    "ichthyology",
    "marine-biology",
    "freshwater",
    "botany",
    "botanical",
    "plant",
    "phytology",
    "mycology",
    "fungal",
    "biophysics",
  ],
  chemistry: [
    "chemistry",
    "chemical",
    "chem",
    "organic",
    "inorganic",
    "analytical",
    "physical-chemistry",
    "polymer",
    "catalysis",
    "electrochemistry",
    "spectroscopy",
    "chromatography",
    "acs",
    "rsc",
    "angewandte",
  ],
  physics: [
    "physics",
    "physical",
    "phys",
    "quantum",
    "particle",
    "nuclear",
    "condensed-matter",
    "optics",
    "optical",
    "photonics",
    "laser",
    "plasma",
    "astrophysics",
    "cosmology",
    "relativity",
    "mechanics",
    "thermodynamics",
    "acoustics",
    "aps",
    "aip",
  ],
  "environmental-science": [
    "environmental",
    "environment",
    "ecology",
    "ecological",
    "conservation",
    "sustainability",
    "climate",
    "atmospheric",
    "pollution",
    "waste",
    "water-resources",
    "renewable",
    "biodiversity",
    "wildlife",
    "natural-resources",
  ],
  "earth-science": [
    "geology",
    "geological",
    "geolog",
    "geophysics",
    "geophysical",
    "geochemistry",
    "mineralogy",
    "petrology",
    "paleontology",
    "palaeontology",
    "sedimentology",
    "stratigraphy",
    "tectonics",
    "seismology",
    "volcanology",
    "hydrology",
    "oceanography",
    "meteorology",
    "climatology",
    "soil-science",
  ],
  agriculture: [
    "agriculture",
    "agricultural",
    "agronomy",
    "crop",
    "horticulture",
    "horticultural",
    "forestry",
    "forest",
    "silviculture",
    "animal-science",
    "livestock",
    "dairy",
    "poultry",
    "aquaculture",
    "fisheries",
    "food-science",
    "nutrition",
  ],
  ecology: [
    "ecology",
    "ecological",
    "ecosystem",
    "conservation",
    "wildlife",
    "biodiversity",
  ],
  engineering: [
    "engineering",
    "engineer",
    "mechanical",
    "civil",
    "electrical",
    "electronic",
    "aerospace",
    "aeronautical",
    "automotive",
    "materials",
    "structural",
    "geotechnical",
    "environmental-engineering",
    "biomedical-engineering",
    "chemical-engineering",
    "industrial",
    "manufacturing",
    "robotics",
    "automation",
    "mechatronics",
    "ieee",
    "asme",
    "asce",
    "aiaa",
  ],
  "computer-science": [
    "computer",
    "computing",
    "computational",
    "software",
    "algorithm",
    "artificial-intelligence",
    "machine-learning",
    "data-science",
    "database",
    "networking",
    "cybersecurity",
    "information-systems",
    "information-technology",
    "acm",
    "sigchi",
    "siggraph",
    "human-computer",
    "hci",
    "graphics",
    "visualization",
  ],
  mathematics: [
    "mathematics",
    "mathematical",
    "math",
    "algebra",
    "geometry",
    "topology",
    "analysis",
    "number-theory",
    "combinatorics",
    "discrete",
    "applied-mathematics",
  ],
  statistics: [
    "statistics",
    "statistical",
    "probability",
    "stochastic",
    "bayesian",
    "regression",
    "sampling",
    "data-analysis",
  ],
  history: [
    "history",
    "historical",
    "histor",
    "ancient",
    "medieval",
    "modern-history",
    "renaissance",
    "enlightenment",
    "colonial",
    "postcolonial",
    "historiography",
  ],
  literature: [
    "literature",
    "literary",
    "poetry",
    "fiction",
    "novel",
    "drama",
    "comparative-literature",
    "english-literature",
    "american-literature",
    "world-literature",
    "rhetoric",
    "composition",
    "mla",
    "modern-language",
  ],
  philosophy: [
    "philosophy",
    "philosophical",
    "ethics",
    "metaphysics",
    "epistemology",
    "logic",
    "aesthetics",
    "phenomenology",
    "existentialism",
    "analytic-philosophy",
  ],
  linguistics: [
    "linguistics",
    "linguistic",
    "language",
    "phonetics",
    "phonology",
    "syntax",
    "semantics",
    "pragmatics",
    "sociolinguistics",
    "psycholinguistics",
    "computational-linguistics",
    "applied-linguistics",
  ],
  "art-history": [
    "art-history",
    "art",
    "visual-arts",
    "fine-arts",
    "museum",
    "gallery",
    "curatorial",
    "aesthetics-art",
    "design",
  ],
  music: [
    "music",
    "musical",
    "musicology",
    "ethnomusicology",
    "composition",
    "performance",
    "music-theory",
    "music-education",
  ],
  "film-media": [
    "film",
    "cinema",
    "media",
    "television",
    "broadcasting",
    "journalism",
    "communication",
    "mass-media",
    "digital-media",
    "multimedia",
  ],
  archaeology: [
    "archaeology",
    "archaeological",
    "archaeolog",
    "prehistory",
    "prehistoric",
    "excavation",
    "antiquity",
    "classical",
    "heritage",
  ],
  law: [
    "law",
    "legal",
    "jurisprudence",
    "constitutional",
    "criminal-law",
    "civil-law",
    "international-law",
    "human-rights",
    "court",
    "judicial",
    "legislation",
    "bluebook",
    "legal-citation",
  ],
  business: [
    "business",
    "management",
    "marketing",
    "accounting",
    "finance",
    "entrepreneurship",
    "organizational",
    "corporate",
    "strategy",
    "operations",
    "supply-chain",
    "human-resources",
    "mba",
    "commerce",
  ],
  architecture: [
    "architecture",
    "architectural",
    "urban",
    "planning",
    "landscape",
    "interior-design",
    "building",
    "construction",
  ],
  religion: [
    "religion",
    "religious",
    "theology",
    "theological",
    "biblical",
    "scripture",
    "christian",
    "catholic",
    "protestant",
    "jewish",
    "islamic",
    "buddhist",
    "hindu",
    "spirituality",
    "church",
    "divinity",
    "seminary",
  ],
  geography: [
    "geography",
    "geographic",
    "cartography",
    "gis",
    "spatial",
    "regional",
    "urban-geography",
    "human-geography",
    "physical-geography",
  ],
  general: [
    "multidisciplinary",
    "interdisciplinary",
    "general",
    "science",
    "nature",
    "plos",
    "springer",
    "elsevier",
    "wiley",
    "taylor-francis",
    "sage",
    "frontiers",
  ],
};

/** Style slug → category slugs (each style can appear in multiple). Explicit overrides take precedence. */
export const STYLE_CATEGORIES: Record<string, string[]> = {
  // Explicit mappings for curated styles
  apa: ["psychology", "education", "sociology"],
  mla: ["literature", "linguistics", "art-history"],
  "chicago-author-date": ["history", "sociology", "general"],
  "chicago-notes-bibliography": ["history", "art-history", "religion"],
  vancouver: ["medicine", "nursing", "public-health"],
  harvard1: ["business", "sociology", "general"],
  ieee: ["engineering", "computer-science", "physics"],
  ama: ["medicine"],
  nature: ["biology", "general"],
  "acs-nano": ["chemistry"],
  "turabian-author-date": ["history", "religion"],
  bibtex: ["general"],
  cell: ["biology", "medicine"],
  "elsevier-harvard": ["general"],
  "springer-basic": ["general"],
  "american-political-science-association": ["political-science"],
  "american-sociological-association": ["sociology"],
  "american-anthropological-association": ["anthropology"],
  "council-of-science-editors": ["biology", "environmental-science"],
  "national-library-of-medicine": ["medicine"],
  "apa-6th-edition": ["psychology", "education"],
  "modern-language-association": ["literature", "linguistics"],
  "chicago-fullnote-bibliography": ["history"],
  "harvard-cite-them-right": ["business", "general"],
  "ieee-with-url": ["engineering", "computer-science"],
  "vancouver-superscript": ["medicine"],
  "american-chemical-society": ["chemistry"],
  "association-for-computing-machinery": ["computer-science"],
  "american-physical-society": ["physics"],
  "american-institute-of-physics": ["physics"],
  "royal-society-of-chemistry": ["chemistry"],
  "biomed-central": ["medicine", "biology"],
  "plos-one": ["biology", "general"],
  frontiers: ["psychology", "general"],
  lancet: ["medicine"],
  bmj: ["medicine"],
  jama: ["medicine"],
  "american-medical-association": ["medicine"],
  science: ["general", "biology"],
  "elsevier-numeric": ["general"],
  "elsevier-with-titles": ["general"],
  "springer-lecture-notes-in-computer-science": ["computer-science"],
  "springer-mathphys": ["mathematics", "physics"],
  "cambridge-university-press-author-date": ["general"],
  "oxford-university-press-author-date": ["general"],
  "oxford-university-press-note": ["history", "law"],
  "wiley-vch-books": ["chemistry"],
  "wiley-author-date": ["general"],
  "taylor-and-francis-chicago-author-date": ["general"],
  "taylor-and-francis-harvard": ["general"],
  "sage-harvard": ["sociology", "education"],
  "sage-vancouver": ["medicine"],
  "sage-apa": ["psychology"],
  "turabian-fullnote-bibliography": ["history", "religion"],
  "cse-name-year": ["biology"],
  "cse-citation-name": ["biology"],
  "cse-citation-sequence": ["biology"],
  "acm-siggraph": ["computer-science"],
  "acm-sigchi": ["computer-science"],
  "american-mathematical-society": ["mathematics"],
  "american-statistical-association": ["statistics"],
  "american-economic-association": ["economics"],
  "american-historical-association": ["history"],
  "american-philosophical-association": ["philosophy"],
  "american-association-of-geographers": ["geography"],
  "ecological-society-of-america": ["ecology", "environmental-science"],
  "geological-society-of-america": ["earth-science"],
  "american-geophysical-union": ["earth-science", "physics"],
  "american-meteorological-society": ["earth-science", "environmental-science"],
  "american-society-of-civil-engineers": ["engineering"],
  "american-society-of-mechanical-engineers": ["engineering"],
  "american-institute-of-aeronautics-and-astronautics": ["engineering"],
  "society-for-neuroscience": ["medicine", "psychology"],
  "american-society-for-biochemistry-and-molecular-biology": [
    "biology",
    "chemistry",
  ],
  "american-society-for-cell-biology": ["biology"],
  "american-society-of-human-genetics": ["biology", "medicine"],
  "american-heart-association": ["medicine"],
  "american-diabetes-association": ["medicine"],
  "american-cancer-society": ["medicine"],
  "american-psychiatric-association": ["psychology", "medicine"],
  "infectious-diseases-society-of-america": ["medicine"],
  "american-society-of-hematology": ["medicine"],
  "radiological-society-of-north-america": ["medicine"],
  "american-academy-of-neurology": ["medicine"],
  "american-society-of-clinical-oncology": ["medicine"],
  "american-association-for-cancer-research": ["medicine"],
  "endocrine-society": ["medicine"],
  optica: ["physics", "engineering"],
  peerj: ["biology", "general"],
  "de-gruyter-author-date": ["general"],
  "american-society-of-agronomy": ["agriculture"],
  "american-society-for-microbiology": ["biology", "medicine"],
  "american-society-of-plant-biologists": ["biology", "agriculture"],
  "british-ecological-society": ["ecology"],
  "society-of-biblical-literature": ["religion"],
  "college-art-association": ["art-history"],
  "american-association-for-the-advancement-of-science": ["general"],
  "institute-of-electrical-and-electronics-engineers": ["engineering"],
  "american-association-of-petroleum-geologists": ["earth-science"],
  "cite-them-right": ["general"],
  "american-thoracic-society": ["medicine"],
  "american-society-of-nephrology": ["medicine"],
  "american-society-of-animal-science": ["agriculture", "veterinary"],
  "crop-science-society-of-america": ["agriculture"],
  "soil-science-society-of-america": ["agriculture", "earth-science"],
  "geophysical-research-letters": ["earth-science"],
  "society-for-research-in-child-development": ["psychology", "education"],
  "association-for-information-systems": ["computer-science", "business"],
  "american-nurses-association": ["nursing"],
  "american-public-health-association": ["public-health"],
  "american-college-of-physicians": ["medicine"],
  "american-roentgen-ray-society": ["medicine"],
  "institute-of-mathematical-statistics": ["statistics", "mathematics"],
  "american-society-of-anesthesiologists": ["medicine"],
  "american-speech-language-hearing-association": ["medicine", "linguistics"],
  "mineralogical-society-of-america": ["earth-science"],
  "society-of-economic-geologists": ["earth-science"],
  "botanical-society-of-america": ["biology", "agriculture"],
  "american-society-of-mammalogists": ["biology"],
  "american-fisheries-society": ["biology", "environmental-science"],
  "american-ornithological-society": ["biology"],
  "society-for-conservation-biology": ["ecology", "environmental-science"],
  "american-musicological-society": ["music"],
  "american-academy-of-religion": ["religion"],
};

/**
 * Intelligently detect categories for a style based on its slug and name.
 * Uses keyword matching to assign relevant categories.
 */
function detectCategoriesFromSlug(slug: string, name: string): string[] {
  const searchText = `${slug} ${name}`
    .toLowerCase()
    .replace(/-/g, " ")
    .replace(/[^a-z0-9\s]/g, "");
  const detected: Set<string> = new Set();

  for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    for (const keyword of keywords) {
      const normalizedKeyword = keyword.replace(/-/g, " ");
      if (searchText.includes(normalizedKeyword)) {
        detected.add(category);
        break; // One keyword match is enough for this category
      }
    }
  }

  // If nothing detected, return "other"
  if (detected.size === 0) {
    return ["other"];
  }

  return Array.from(detected);
}

/**
 * Detection is keyword matching over every category for every style, so the
 * answer is cached per slug. Without this, one `getStylesByCategory` call costs
 * a full detection pass over all 10,834 styles — and the site renders one page
 * per category, plus markdown and API views of each.
 */
const CATEGORIES_BY_SLUG = new Map<string, string[]>();

export function getCategoriesForStyle(slug: string): string[] {
  const cached = CATEGORIES_BY_SLUG.get(slug);
  if (cached) {
    return cached;
  }
  // Explicit mapping wins over detection.
  const explicit = STYLE_CATEGORIES[slug];
  if (explicit) {
    CATEGORIES_BY_SLUG.set(slug, explicit);
    return explicit;
  }
  const style = getCitationStyleBySlug(slug);
  const detected = style
    ? detectCategoriesFromSlug(slug, style.name)
    : ["other"];
  CATEGORIES_BY_SLUG.set(slug, detected);
  return detected;
}

export function getStylesByCategory(categorySlug: string): CitationStyleMeta[] {
  return CITATION_STYLES.filter((s) =>
    getCategoriesForStyle(s.slug).includes(categorySlug)
  );
}
